import { Action, combineReducers, configureStore, createListenerMiddleware, ThunkAction } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';
import { FLUSH, PAUSE, PERSIST, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import api from '../app/api';
import authReducer from '../modules/auth/store/auth-slice';
import clientGroupsReducer from '../modules/client-groups/store/client-groups-slice';
import clientUsersReducer from '../modules/client-users/store/client-users-slice';
import currentUserReducer from '../modules/user/store/current-user-slice';
import customersReducer from '../modules/customers/store/customers-slice';
import requestsReducer from '../modules/request/store/requests-slice';
import scanRequestsReducer from '../modules/scan-requests/store/scan-requests-slice';
import signRequestsReducer from '../modules/sign-requests/store/sign-requests-slice';
import uiReducer from '../modules/ui/store/ui-slice';
import { resetState } from './actions';

const listenerMiddleware = createListenerMiddleware();

listenerMiddleware.startListening({
  actionCreator: resetState,
  effect: (action, listenerApi) => {
    listenerApi.dispatch(api.util.resetApiState());
  },
});

const rootReducer = combineReducers({
  auth: authReducer,
  clientGroups: clientGroupsReducer,
  ui: uiReducer,
  customers: customersReducer,
  clientUsers: clientUsersReducer,
  currentUser: currentUserReducer,
  scanRequests: scanRequestsReducer,
  signRequests: signRequestsReducer,
  requests: requestsReducer,
  [api.reducerPath]: api.reducer,
});

export const setupStore = (preloadedState?: Partial<RootState>) => {
  return configureStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(listenerMiddleware.middleware, api.middleware),
    enhancers: getDefaultEnhancers => {
      const defaultEnhancers = getDefaultEnhancers();
      const sentryEnhancer = process.env.REACT_APP_SENTRY_DSN ? Sentry.createReduxEnhancer() : undefined;

      return sentryEnhancer ? defaultEnhancers.concat(sentryEnhancer) : defaultEnhancers;
    },
    devTools: process.env.NODE_ENV === 'development',
    preloadedState,
  });
};

export const store = setupStore();
export const storePersistor = persistStore(store);

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
